import React from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { gql, useQuery } from '@apollo/client'
import { Alert, Button, Drawer, Table } from 'antd'
import dayjs from 'dayjs'

import CapacityForm from './CapacityForm'

const PAGE_SIZE = 50
const UNASSIGNED = 'unassigned'

const ASSIGNEE_DEALS_QUERY = gql`
  query LeadDistributionAssigneeDeals($assigneeId: String, $assignDate: ISO8601Date, $first: Int, $after: String) {
    deals(
      state: "all"
      sortOption: ""
      filterOption: ""
      assigneeId: $assigneeId
      assignDate: $assignDate
      first: $first
      after: $after
    ) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      nodes {
        id
        assignDate
        assigneeType
        createdAt
        assignor {
          id
          fullName
        }
        order {
          id
          number
          state
          stateLabel
          cancelReasonName
          grandTotal {
            display
          }
        }
      }
    }
  }
`

const cancelReasonOf = (order) => {
  const reason = order.cancelReasonName
  if (order.state !== 'cancel' || !reason || reason.startsWith('{')) return null
  return reason
}

const AssigneeOrdersDrawer = ({ assigneeId, date, isSuperAdmin, onCapacitySaved, onClose, row }) => {
  const { i18n } = useTranslation('leadDistributions')
  const open = !!assigneeId

  const { loading, data, error, fetchMore } = useQuery(ASSIGNEE_DEALS_QUERY, {
    variables: { assigneeId, assignDate: date, first: PAGE_SIZE },
    skip: !open,
    fetchPolicy: 'cache-and-network'
  })

  const deals = (data && data.deals) || null
  const nodes = (deals && deals.nodes) || []
  const isUnassigned = assigneeId === UNASSIGNED
  const assigneeName = isUnassigned
    ? i18n.t('leadDistributions:drawer.nobody')
    : (row && row.assignee && row.assignee.fullName) || `#${assigneeId}`

  const onLoadMore = () => {
    fetchMore({
      variables: { after: deals.pageInfo.endCursor },
      updateQuery: (previous, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previous

        return {
          deals: {
            ...fetchMoreResult.deals,
            nodes: [...previous.deals.nodes, ...fetchMoreResult.deals.nodes]
          }
        }
      }
    })
  }

  const renderState = (order) => {
    const label = order.stateLabel || order.state
    const reason = cancelReasonOf(order)
    return reason ? `${label} (${reason})` : label
  }

  const renderDistributor = (deal) => {
    const assignorName = (deal.assignor && deal.assignor.fullName) || '-'
    if (deal.assigneeType === 'system') {
      return i18n.t('leadDistributions:drawer.systemAssignor', { name: assignorName })
    }
    return assignorName
  }

  const columns = [
    {
      key: 'number',
      title: i18n.t('leadDistributions:drawer.columns.number'),
      render: deal => <Link to={`/inquiries/${deal.order.number}`}>{deal.order.number}</Link>
    },
    {
      key: 'state',
      title: i18n.t('leadDistributions:drawer.columns.state'),
      render: deal => renderState(deal.order)
    },
    {
      key: 'distributor',
      title: i18n.t('leadDistributions:drawer.columns.distributor'),
      render: renderDistributor
    },
    {
      key: 'grandTotal',
      align: 'right',
      title: i18n.t('leadDistributions:drawer.columns.grandTotal'),
      render: deal => (deal.order.grandTotal && deal.order.grandTotal.display) || '-'
    },
    {
      key: 'createdAt',
      title: i18n.t('leadDistributions:drawer.columns.createdAt'),
      render: deal => (deal.createdAt ? dayjs(deal.createdAt).format('YYYY-MM-DD HH:mm') : '-')
    }
  ]

  const canEditCapacity = isSuperAdmin && !isUnassigned && !!row

  return (
    <Drawer
      onClose={onClose}
      open={open}
      title={`${assigneeName} : ${date}`}
      width={Math.min(960, typeof window === 'undefined' ? 960 : window.innerWidth)}
    >
      {canEditCapacity && (
        <div style={{ marginBottom: 16 }}>
          <CapacityForm
            key={`${row.assignee.id}-${row.leadAmountPerDay}`}
            initialValue={row.leadAmountPerDay}
            label={i18n.t('leadDistributions:capacity.staffLabel', { name: row.assignee.fullName })}
            onSaved={onCapacitySaved}
            staffId={row.assignee.id}
          />
        </div>
      )}
      {error && <Alert message={error.message} showIcon style={{ marginBottom: 16 }} type="error" />}
      <Table
        columns={columns}
        dataSource={nodes}
        loading={loading}
        locale={{ emptyText: i18n.t('leadDistributions:drawer.empty') }}
        pagination={false}
        rowKey="id"
        scroll={{ x: true }}
        size="small"
      />
      {deals && deals.pageInfo.hasNextPage && (
        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <Button loading={loading} onClick={onLoadMore}>
            {i18n.t('leadDistributions:drawer.loadMore', { shown: nodes.length, total: deals.totalCount })}
          </Button>
        </div>
      )}
    </Drawer>
  )
}

AssigneeOrdersDrawer.propTypes = {
  assigneeId: PropTypes.string,
  date: PropTypes.string.isRequired,
  isSuperAdmin: PropTypes.bool,
  onCapacitySaved: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  row: PropTypes.object
}

AssigneeOrdersDrawer.defaultProps = {
  assigneeId: null,
  isSuperAdmin: false,
  onCapacitySaved: null,
  row: null
}

export default AssigneeOrdersDrawer
